import DesignNavigationItem from "./designNavigationItem";
import {Feature} from "../../interfaces/Feature";

/**
 * A "unified" navigation item.
 *
 * Unlike a plain DesignNavigationItem (which wraps a single, already resolved
 * feature), a UNavItem is precomputed once per layout at load time. It groups
 * together every feature that shares the same getSplittedFeatureName() so that,
 * when the navigation is requested for a concrete scenario, we only have to
 * pick the correct `part` out of `uFeatures` (by filters / conditions) instead
 * of rebuilding the whole nav list from scratch.
 *
 * @extends DesignNavigationItem
 */
export default class UNavItem extends DesignNavigationItem {
  /**
   * Unique identifier of the item, usually taken from getSplittedFeatureName()
   * (which is normally derived from the sharedFeatureName).
   * @type {string}
   */
  uNameId: string = "";

  /**
   * All features that share the same uNameId (all layouts / scenes / positions).
   * `part` is always one concrete feature taken from this list.
   * @type {[featureInterface]}
   */
  uFeatures: Feature[] = [];

  /**
   * When the item has no feature matching the current conditions but does have
   * a feature that lives in a different room layout, we keep a reference to it
   * so the navigation can hint that the item is available elsewhere.
   * @type {featureInterface|null}
   */
  availableInAnotherLayout: Feature = null;

  /**
   * Set when no uFeature matches the current conditions but one exists in the
   * SAME room layout (only failing other meta like ShowerSize / RoomScene). The
   * item can't render now, but it is NOT "available in another layout" either.
   * @type {featureInterface|null}
   */
  notMatchingMeta: Feature = null;

  /**
   * Scenario resolved "not in visualizer" state, set by getUNavigationItems(): true when no
   * uFeature can render with the current conditions (no layers, or only matching another room
   * layout / size / scene). Read it via isNavItemNotInVisualizer(navItem).
   * `part.notAvailableInVisualizer` stays the STATIC flag (no layers) and is never rewritten.
   * null until resolved (then isNavItemNotInVisualizer falls back to the part's static flag).
   * @type {boolean|null}
   */
  cantRenderInVisualizer: boolean = null;

  /**
   * @param uNameId {string}                required, unique identifier
   * @param part {featureInterface}         required, the currently active feature (price / cost / layer source)
   * @param uFeatures {[featureInterface]}  all features sharing the same uNameId (may be empty)
   */
  constructor(uNameId, part, uFeatures = []) {
    super(part ? part.featureName : "", part);
    this.uNameId = uNameId;
    this.uFeatures = uFeatures || [];
  }

  /**
   * Swaps the active feature (and the derived fields) without recreating the item.
   * @param feature {featureInterface}
   */
  setPart(feature: Feature) {
    if (!feature) {
      return this;
    }
    this.part = feature;
    this.text = feature.featureName;
    this.matchesMetaFailed = !!feature.matchesMetaFailed || false;
    return this;
  }

  /**
   * Shallow copy used to build a scenario specific item from the cached master
   * item without mutating the cached one.
   * @param copyFeatures {boolean} false when the caller replaces `uFeatures` right away (getUNavigationItems)
   * @returns {UNavItem}
   */
  clone(copyFeatures = true): UNavItem {
    const cloned = Object.assign(Object.create(Object.getPrototypeOf(this)), this);
    if (copyFeatures) {
      cloned.uFeatures = this.uFeatures.slice();
    }
    return cloned;
  }
}
